import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { Loader2, Star, Image as ImageIcon, ArrowRight } from "lucide-react";

const STATUS_COLOR = {
  pending_payment: "bg-yellow-500 text-black",
  awaiting_contractor: "bg-blue-500 text-white",
  accepted: "bg-[#8DE038] text-[#050806]",
  completed: "bg-[#1F3325] text-[#8DE038]",
  cancelled: "bg-red-500/30 text-red-300",
};

function StatusBadge({ status }) {
  return <span className={`px-2 py-1 text-[10px] font-display uppercase tracking-widest ${STATUS_COLOR[status] || ""}`}>{status.replace(/_/g, " ")}</span>;
}

function ReviewModal({ booking, onClose, onSubmit }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    setLoading(true); setErr("");
    try { await onSubmit({ rating, comment }); onClose(); }
    catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center px-5" onClick={onClose}>
      <div onClick={(e)=>e.stopPropagation()} className="bg-[#0F1712] border border-[#1F3325] p-8 w-full max-w-md" data-testid="review-modal">
        <h3 className="font-display uppercase text-2xl text-[#F0F4F1]">Rate the work</h3>
        <p className="text-[#9CAEA3] text-sm mt-1">{booking.service} · {booking.contractor_name || "Pro"}</p>
        <div className="mt-5 flex gap-2">
          {[1,2,3,4,5].map((r) => (
            <button key={r} onClick={()=>setRating(r)} data-testid={`star-${r}`} className="p-1">
              <Star size={32} className={r <= rating ? "fill-[#8DE038] text-[#8DE038]" : "text-[#1F3325]"}/>
            </button>
          ))}
        </div>
        <textarea data-testid="review-comment" rows={4} className="gl-input mt-4 resize-none" placeholder="How was the service? (optional)" value={comment} onChange={(e)=>setComment(e.target.value)}/>
        {err && <div className="text-red-400 text-sm mt-3">{err}</div>}
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="btn-secondary text-xs flex-1 justify-center">Cancel</button>
          <button onClick={submit} disabled={loading} data-testid="submit-review" className="btn-primary text-xs flex-1 justify-center">
            {loading ? <Loader2 className="animate-spin" size={14}/> : "Submit review"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/bookings/mine"); setBookings(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitReview = async (payload) => {
    await api.post(`/bookings/${reviewing.id}/review`, payload);
    await load();
  };

  return (
    <div data-testid="my-bookings-page" className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ My Bookings</div>
            <h1 className="font-display uppercase tracking-tighter text-4xl md:text-6xl text-[#F0F4F1] heading-accent">Your jobs</h1>
          </div>
          <Link to="/book" className="btn-primary text-xs">New Booking <ArrowRight size={14}/></Link>
        </div>
        {loading && <div className="text-[#9CAEA3] flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>}
        {!loading && bookings.length === 0 && (
          <div className="border border-dashed border-[#1F3325] p-12 text-center text-[#9CAEA3]">
            No bookings yet. <Link to="/book" className="text-[#8DE038]">Book your first job →</Link>
          </div>
        )}
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} data-testid={`booking-${b.id}`} className="border border-[#1F3325] bg-[#0F1712] p-5 grid md:grid-cols-12 gap-4">
              <div className="md:col-span-3">
                <div className="font-display uppercase text-xs text-[#8DE038] tracking-widest">{b.preferred_date} · {b.preferred_time}</div>
                <div className="font-display uppercase text-xl text-[#F0F4F1] mt-1">{b.service}</div>
                <div className="text-[#9CAEA3] text-sm">£{b.estimated_value_gbp} estimated</div>
              </div>
              <div className="md:col-span-5 text-sm text-[#C8D4CB] space-y-1">
                <div>📍 {b.address}, {b.postcode}</div>
                {b.contractor_name && <div>👤 Pro: <span className="text-[#F0F4F1]">{b.contractor_name}</span></div>}
                {b.notes && <div className="text-[#9CAEA3] italic">"{b.notes}"</div>}
                {b.photo_urls?.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {b.photo_urls.slice(0,4).map((u, i) => (<img key={i} src={u} alt="" className="w-12 h-12 object-cover border border-[#1F3325]"/>))}
                    {b.photo_urls.length > 4 && <div className="w-12 h-12 border border-[#1F3325] flex items-center justify-center text-xs text-[#9CAEA3]">+{b.photo_urls.length - 4}</div>}
                  </div>
                )}
              </div>
              <div className="md:col-span-2"><StatusBadge status={b.status}/></div>
              <div className="md:col-span-2 flex flex-col gap-2">
                <div className="text-xs text-[#9CAEA3]">Fee: £{b.platform_fee_gbp}</div>
                {b.status === "completed" && (
                  <button data-testid={`leave-review-${b.id}`} onClick={()=>setReviewing(b)} className="btn-primary text-[10px] justify-center py-2">
                    <Star size={12}/> Leave review
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {reviewing && <ReviewModal booking={reviewing} onClose={()=>setReviewing(null)} onSubmit={submitReview}/>}
    </div>
  );
}
